import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { List, FileText, Bank, MapPin, EnvelopeSimple, SignOut, IdentificationCard, Receipt } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-800", in_transit: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-800",
  unpaid: "bg-muted text-muted-foreground",
};

const ENTITY_OPTS = [
  ["individual", "Individual (SSN)"],
  ["sole_prop", "Sole Proprietor"],
  ["single_llc", "Single-member LLC"],
  ["c_corp", "C-Corporation"],
  ["s_corp", "S-Corporation"],
  ["partnership", "Partnership"],
];

function W9Form({ onDone, initial }) {
  const [f, setF] = useState(initial || { legal_name: "", entity_type: "individual", tin: "", address_line1: "", address_line2: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const u = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/profile/w9", f); toast.success("Tax info & address saved"); onDone?.(); }
    catch (er) { setErr(formatApiErrorDetail(er.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="w9-form">
      <div><Label className="label-caps">Legal name</Label><Input className="rounded-none mt-1" value={f.legal_name} onChange={u("legal_name")} required data-testid="w9-legal-name" /></div>
      <div>
        <Label className="label-caps">Tax classification</Label>
        <select className="w-full rounded-none mt-1 h-10 border border-input bg-background px-3 text-sm" value={f.entity_type} onChange={u("entity_type")} data-testid="w9-entity">
          {ENTITY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div><Label className="label-caps">SSN or Tax ID (EIN)</Label><Input className="rounded-none mt-1" value={f.tin} onChange={u("tin")} placeholder="XXX-XX-XXXX" required data-testid="w9-tin" /></div>
      <div><Label className="label-caps">Street address</Label><Input className="rounded-none mt-1" value={f.address_line1} onChange={u("address_line1")} required data-testid="w9-addr1" /></div>
      <Input className="rounded-none" value={f.address_line2} onChange={u("address_line2")} placeholder="Apt / Suite (optional)" data-testid="w9-addr2" />
      <div className="grid grid-cols-3 gap-2">
        <Input className="rounded-none" value={f.city} onChange={u("city")} placeholder="City" required data-testid="w9-city" />
        <Input className="rounded-none" value={f.state} onChange={u("state")} placeholder="ST" maxLength={2} required data-testid="w9-state" />
        <Input className="rounded-none" value={f.zip} onChange={u("zip")} placeholder="ZIP" required data-testid="w9-zip" />
      </div>
      {err && <div className="text-destructive text-sm font-mono" data-testid="w9-error">{err}</div>}
      <Button type="submit" disabled={busy} className="w-full rounded-none" data-testid="w9-submit">{busy ? "Saving…" : "Save"}</Button>
    </form>
  );
}

export function ContractorMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [w9Done, setW9Done] = useState(true);
  const [showW9, setShowW9] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [email, setEmail] = useState(user?.email || "");

  const check = useCallback(async () => {
    try { setW9Done((await api.get("/profile/w9")).data.w9_completed); } catch { /* silent */ }
  }, []);
  useEffect(() => { check(); }, [check]);

  const openHistory = async () => {
    setOpen(false); setShowHistory(true); setHistory(null);
    try { setHistory((await api.get("/pay-reports/history")).data.reports || []); }
    catch { setHistory([]); }
  };

  const saveEmail = async () => {
    try { await api.post("/profile/email", { email }); toast.success("Email updated"); setShowEmail(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update email"); }
  };
  const go = (hash) => { setOpen(false); navigate("/"); setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 250); };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="p-2 hover:bg-muted transition-colors" data-testid="contractor-menu-button" aria-label="Menu"><List size={22} /></button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <nav className="mt-6 flex flex-col">
            {[
              ["Tax Documents", <FileText size={18} />, () => go('[data-testid="tax-form-card"]')],
              ["Payout History", <Receipt size={18} />, openHistory],
              ["Direct Deposit", <Bank size={18} />, () => go('[data-testid="direct-deposit-card"]')],
              ["Tax Info & Address", <IdentificationCard size={18} />, () => { setOpen(false); setShowW9(true); }],
              ["Update Address", <MapPin size={18} />, () => { setOpen(false); setShowW9(true); }],
              ["Update Email", <EnvelopeSimple size={18} />, () => { setOpen(false); setEmail(user?.email || ""); setShowEmail(true); }],
            ].map(([label, icon, fn]) => (
              <button key={label} onClick={fn} className="flex items-center gap-3 px-2 py-3 text-left text-sm border-b border-border hover:bg-muted transition-colors" data-testid={`menu-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {icon} {label}
              </button>
            ))}
            <button onClick={logout} className="flex items-center gap-3 px-2 py-3 text-left text-sm text-destructive hover:bg-muted transition-colors mt-4" data-testid="menu-logout"><SignOut size={18} /> Sign out</button>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Blocking gate: contractors must provide SSN/Tax ID + address */}
      <Dialog open={!w9Done} onOpenChange={() => {}}>
        <DialogContent className="rounded-none [&>button]:hidden" data-testid="w9-gate">
          <DialogHeader><DialogTitle>Complete your tax info</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Before you continue, please provide your SSN or Tax ID and mailing address. This is required for your 1099 and payments.</p>
          <W9Form onDone={() => { setW9Done(true); check(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={showW9} onOpenChange={setShowW9}>
        <DialogContent className="rounded-none" data-testid="w9-edit-dialog">
          <DialogHeader><DialogTitle>Tax info & address</DialogTitle></DialogHeader>
          <W9Form onDone={() => { setShowW9(false); check(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="rounded-none" data-testid="email-dialog">
          <DialogHeader><DialogTitle>Update email</DialogTitle></DialogHeader>
          <Label className="label-caps">New email</Label>
          <Input type="email" className="rounded-none mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input-new" />
          <Button className="rounded-none mt-2" onClick={saveEmail} data-testid="email-save">Save email</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="rounded-none max-w-md" data-testid="payout-history-dialog">
          <DialogHeader><DialogTitle>Payout History</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            {history === null && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="payout-history-loading">Loading…</div>}
            {history !== null && history.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="payout-history-empty">No pay periods with worked hours yet.</div>}
            {history !== null && history.map((r) => (
              <div key={r.period_start} className="flex items-center justify-between border-b border-border py-3" data-testid={`payout-history-row-${r.period_start}`}>
                <div>
                  <div className="text-sm font-medium">{r.period_start} → {r.period_end}</div>
                  <div className="text-xs text-muted-foreground">{(r.total_hours ?? 0).toFixed(2)} hrs · {money(r.gross_pay)}</div>
                </div>
                <span className={`text-[0.65rem] uppercase tracking-wide px-2 py-1 ${STATUS_STYLE[r.status] || STATUS_STYLE.unpaid}`} data-testid={`payout-history-status-${r.period_start}`}>
                  {(r.status || "unpaid").replace("_", " ")}{r.payment_method ? ` · ${r.payment_method}` : ""}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
